import { useCallback, useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { z } from 'zod';

const favoriteWorkflowsSchema = z.array(z.string());

const storeKey = (userId: string) => `${userId}-favoriteWorkflows`;

const addFavorite = (favorites: string[], rowId: string): string[] =>
  favorites.includes(rowId) ? favorites : [...favorites, rowId];

const removeFavorite = (favorites: string[], rowId: string): string[] =>
  favorites.filter(id => id !== rowId);

const readFavorites = (userId: string): string[] => {
  try {
    const parsed = favoriteWorkflowsSchema.safeParse(
      JSON.parse(localStorage.getItem(storeKey(userId)) || '[]')
    );
    if (!parsed.success) {
      // Malformed-but-readable storage is user-side corruption, not an app
      // bug — warn locally (with context) instead of flooding Sentry, then
      // reset to an empty list.
      console.warn(
        '[useWorkflowFavorites] ignoring malformed favorites',
        { userId },
        parsed.error.issues
      );
      return [];
    }
    return parsed.data;
  } catch (error) {
    // An actual throw (e.g. corrupt JSON) is unexpected — report it with the
    // user id so it can be traced.
    Sentry.captureException(error, {
      tags: { hook: 'useWorkflowFavorites' },
      extra: { userId }
    });
    return [];
  }
};

export const useWorkflowFavorites = (userId: string) => {
  const [favIds, setFavIds] = useState<string[]>(() => readFavorites(userId));

  // O(1) membership lookups for isFavorite (called per card on every render).
  const favSet = useMemo(() => new Set(favIds), [favIds]);

  const toggleFavorite = useCallback(
    (rowId: string, value: boolean) => {
      setFavIds(prev => {
        const next = value
          ? addFavorite(prev, rowId)
          : removeFavorite(prev, rowId);
        try {
          localStorage.setItem(storeKey(userId), JSON.stringify(next));
        } catch (error) {
          Sentry.captureException(error, {
            tags: { hook: 'useWorkflowFavorites' },
            extra: { userId }
          });
          toast.error(
            "Couldn't save your favorite — it may not persist after a reload."
          );
        }
        return next;
      });
    },
    [userId]
  );

  const isFavorite = useCallback(
    (rowId: string) => favSet.has(rowId),
    [favSet]
  );

  return { favIds, isFavorite, toggleFavorite };
};
